import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const popRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/me/notifications", { params: { limit: 25 } });
      setItems(r.data.items); setUnread(r.data.unread);
    } catch { /* unauthenticated routes etc. */ }
  };

  // Load on mount and every route change (spec: fresh on navigation).
  useEffect(() => { load(); }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (!popRef.current) return;
      if (!popRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    await api.post(`/me/notifications/${id}/read`);
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((n) => Math.max(0, n - 1));
  };
  const markAll = async () => { await api.post("/me/notifications/read-all"); load(); };

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded hover:bg-slate-100 text-slate-700"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="notification-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 shadow-xl z-50" data-testid="notification-popover">
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <div className="text-xs uppercase tracking-widest text-slate-500">Notifications</div>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={markAll} data-testid="notif-mark-all"><CheckCheck className="w-3 h-3 mr-1" />Mark all</Button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {items.length === 0 && <p className="p-6 text-center text-xs text-slate-500">You're all caught up.</p>}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n.id)}
                className={`block w-full text-left p-3 hover:bg-slate-50 ${n.read ? "" : "bg-indigo-50/40"}`}
                data-testid={`notif-row-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-indigo-600 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>}
                    <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
